import React from "react";
import { Plus } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import Tooltip from "../ui/Tooltip";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

const BodyContactExclusionsSection = React.memo(function BodyContactExclusionsSection({
  authoringLocked,
  bodyOptions,
  contactExcludePartnerOptions,
  contactExcludesForBody,
  isAddingContactExclude,
  newContactExcludeBodyUuid,
  onAddContactExclude,
  onCancelAddContactExclude,
  onNewContactExcludeBodyUuidChange,
  onRemoveContactExclude,
  onRequestAddContactExclude,
  onUpdateContactExcludePartner,
  selectedBodyUuid,
}) {
  const selectedPartnerUuid =
    newContactExcludeBodyUuid || contactExcludePartnerOptions[0]?.value || "";

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
      <Collapsible defaultOpen={false}>
        <CollapsibleTrigger
          quiet
          aria-label="Toggle Contact Exclusions"
          actions={
            <>
              <InfoTooltip
                text="Contact exclusions are body-pair overrides: they disable contacts between this body and one selected partner body. For broader contact behavior, collision groups, or solver/contact defaults, use Physics Settings."
                widthClass="w-72"
              />
              <Tooltip
                content={
                  authoringLocked
                    ? "Stop physics to add a contact exclusion"
                    : "Add contact exclusion"
                }
              >
                <button
                  type="button"
                  disabled={authoringLocked || contactExcludePartnerOptions.length === 0}
                  aria-label="Add contact exclusion"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRequestAddContactExclude();
                  }}
                  className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                  style={{
                    opacity:
                      authoringLocked || contactExcludePartnerOptions.length === 0 ? 0.35 : 0.55,
                  }}
                >
                  <Plus size={16} aria-hidden="true" />
                </button>
              </Tooltip>
            </>
          }
          actionsClassName="gap-1"
        >
          <span className="min-w-0 truncate text-left">
            Contact Exclusions ({contactExcludesForBody.length})
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3">
            {isAddingContactExclude ? (
              <div className="space-y-2 rounded border border-gray-700 bg-gray-900/40 p-2">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wide panel-text-helper">
                    Select Body
                  </div>
                  <MenuSelect
                    size="compact"
                    value={selectedPartnerUuid}
                    onChange={onNewContactExcludeBodyUuidChange}
                    disabled={authoringLocked || contactExcludePartnerOptions.length === 0}
                    placeholder="Select body"
                    options={contactExcludePartnerOptions}
                    aria-label="Contact exclusion partner body"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-gray-600 bg-gray-900 px-2 py-1 text-xs text-gray-300 hover:bg-gray-800"
                    onClick={() => {
                      onCancelAddContactExclude();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`rounded border border-gray-600 bg-gray-900 px-2 py-1 text-xs text-gray-200 hover:bg-gray-800 ${
                      authoringLocked || !selectedPartnerUuid
                        ? "cursor-not-allowed opacity-50 hover:bg-gray-900"
                        : ""
                    }`}
                    disabled={authoringLocked || !selectedPartnerUuid}
                    onClick={onAddContactExclude}
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : null}

            {contactExcludesForBody.length === 0 ? (
              <div className="rounded border border-gray-700 bg-gray-900/40 px-2 py-2 text-xs text-gray-400">
                No manual contact exclusions for this body.
              </div>
            ) : null}

            {contactExcludesForBody.map((exclude) => {
              const partnerUuid =
                String(exclude.body1) === String(selectedBodyUuid)
                  ? String(exclude.body2)
                  : String(exclude.body1);
              const excludedByOther = new Set(
                contactExcludesForBody
                  .filter((other) => other.index !== exclude.index)
                  .map((other) =>
                    String(other.body1) === String(selectedBodyUuid)
                      ? String(other.body2)
                      : String(other.body1)
                  )
              );
              const partnerExists = bodyOptions.some((option) => option.value === partnerUuid);
              const partnerOptions = bodyOptions.filter(
                (option) =>
                  option.value !== String(selectedBodyUuid) && !excludedByOther.has(option.value)
              );
              return (
                <div
                  key={`contact-exclude-${exclude.index}`}
                  className="group rounded border border-gray-700 bg-gray-900/40 p-2"
                >
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                    <div className="min-w-0 space-y-1">
                      <div className="text-[10px] uppercase tracking-wide panel-text-helper">
                        Select Body
                      </div>
                      <MenuSelect
                        size="compact"
                        value={partnerUuid}
                        onChange={(value) => onUpdateContactExcludePartner(exclude, value)}
                        disabled={authoringLocked}
                        options={
                          partnerExists
                            ? partnerOptions
                            : [{ value: partnerUuid, label: "Missing body" }, ...partnerOptions]
                        }
                        aria-label="Contact exclusion partner body"
                      />
                    </div>
                    <PanelDeleteIconButton
                      ariaLabel="Remove contact exclusion"
                      disabled={authoringLocked}
                      onClick={() => onRemoveContactExclude(exclude.index)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );
});

export default BodyContactExclusionsSection;
